import * as React from "react";

import { Center, Code, Select, Stack } from "@mantine/core";
import _ from "lodash";
import objectHash from "object-hash";

import { useAppState } from "../../../hooks/useAppState";
import { AddonTemplateChecklistDocumentElement } from "../../../lib/model/addonTemplate";
import { isSerializedChecklist } from "../../../lib/model/checklistSerialized";
import { serializedAppState, serializedChecklistDocument } from "../../../lib/state/convert";
import { editorEntry } from "../../../lib/state/editor";
import { guessChecklistRole } from "../../../lib/utils/nearestRole";

type StateSelector = Parameters<typeof useAppState>[0];
const selectorNames = [
  "Checklist Document",
  "Serialized Checklist Document Contents",
  "Serialized Checklist Document",
  "Template Designer",
  "Library Entry",
  "History Lengths",
  "Library",
  "All State",
  "Hashes (Library + Editor)",
  "Object sizes (first level)",
  "Computed Local Storage Size",
] as const;
type StateSelectorName = typeof selectorNames[number];
const stateSelectors: Record<StateSelectorName, StateSelector> = {
  "Checklist Document": (state) => editorEntry(state.library)?.present,
  "Serialized Checklist Document Contents": (state) => {
    const editor = editorEntry(state.library);
    return editor !== null ? serializedAppState(editor.present.contents) : null;
  },
  "Serialized Checklist Document": (state) => {
    const editor = editorEntry(state.library);
    return editor !== null ? serializedChecklistDocument(editor.present) : null;
  },
  "Template Designer": (state): AddonTemplateChecklistDocumentElement[] => {
    const editor = editorEntry(state.library);
    if (editor === null) {
      return [];
    }

    const serialized = serializedChecklistDocument(editor.present);
    const templates = serialized.contents.checklists.map((checklist) => {
      if (isSerializedChecklist(checklist)) {
        return {
          ...checklist,
          roles: [guessChecklistRole(checklist.title)],
        };
      } else {
        return checklist;
      }
    });
    return templates;
  },
  "Library Entry": (state) => editorEntry(state.library),
  "History Lengths": (state) => ({
    past: editorEntry(state.library)?.past.length,
    future: editorEntry(state.library)?.future.length,
  }),
  Library: (state) => state.library,
  "All State": (state) => state,
  "Hashes (Library + Editor)": (state) => ({
    editor: {
      id: editorEntry(state.library)?.present.id,
      name: editorEntry(state.library)?.present.title,
      hash: objectHash(editorEntry(state.library)?.present ?? null),
    },
    library: state.library.entries.map((e) => ({
      id: e.present.id,
      name: e.present.title,
      hash: objectHash(e.present),
    })),
  }),
  "Object sizes (first level)": (state) =>
    _.entries(state).map(([k, v]) => [k, JSON.stringify(v).length]),
  "Computed Local Storage Size": () => {
    const localStorageSpace = function () {
      let allStrings = "";
      Object.keys(window.localStorage).forEach((key) => {
        if (key in window.localStorage) {
          allStrings += window.localStorage[key];
        }
      });
      return allStrings ? 3 + (allStrings.length * 16) / (8 * 1024) + " KB" : "Empty (0 KB)";
    };
    return localStorageSpace();
  },
} as const;

type JsonReplacers = Partial<Record<StateSelectorName, Parameters<typeof JSON.stringify>[1]>>;
const jsonReplacers: JsonReplacers = {
  "Template Designer": ["title", "roles", "content", "header", "item", "setting"],
};

const isSelectorName = (s: string): s is StateSelectorName => {
  return !!_.find(selectorNames, (sn) => sn === s);
};

export const ChecklistDebugState = (): JSX.Element => {
  const [stateSelector, setStateSelector] = React.useState<StateSelectorName>(selectorNames[0]);

  const stateFragment = useAppState(stateSelectors[stateSelector]);
  const onChange = React.useCallback(
    (newValue: string | null) => {
      if (!!newValue && isSelectorName(newValue)) {
        setStateSelector(newValue);
      }
    },
    [setStateSelector]
  );

  const replacer = jsonReplacers[stateSelector];

  return (
    <Stack>
      <Center pt={"md"}>
        <Select
          label="State Fragment"
          value={stateSelector}
          data={selectorNames.map((n) => ({ value: n, label: n }))}
          onChange={onChange}
        />
      </Center>
      <Code block>{JSON.stringify(stateFragment, replacer, 2)}</Code>
    </Stack>
  );
};
